import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../app/auth'
import { OrderCard } from './OrderCard'
import { orderMatchesText } from './orderSearch'
import { useOrdersList } from './useOrders'
import { groupByColumn, localYmd, WORKSHOP_COLUMNS, WORKSHOP_FILTERS, type WorkshopFilter } from './workshop'

/**
 * Werkstatt: das Status-Board (Kanban) aus dem Alt-Monolithen. Vier Spalten (Offen/In Arbeit/Wartet/
 * Fertig), Filter-Chips, Suche und „Neuer Auftrag" in der Filterzeile — wie in der SOURCE.
 */
export function WorkshopBoard() {
  const { user } = useAuth()
  const { data, isLoading, error } = useOrdersList()
  const [filter, setFilter] = useState<WorkshopFilter>('alle')
  const [query, setQuery] = useState('')
  // Heute einmal beim Mounten festhalten (für den Überfällig-Filter) statt bei jedem Render neu.
  const today = useMemo(() => localYmd(new Date()), [])

  const columns = useMemo(() => {
    const all = data?.orders ?? []
    const q = query.trim().toLowerCase()
    const searched = q ? all.filter((order) => orderMatchesText(order, q)) : all
    return groupByColumn(searched, filter, user?.id ?? -1, today)
  }, [data, query, filter, user, today])

  if (isLoading) return <div className="muted">Aufträge werden geladen…</div>
  if (error) {
    return (
      <div className="error" role="alert">
        Aufträge konnten nicht geladen werden.
      </div>
    )
  }

  return (
    <section className="wf-view">
      <div>
        <h2 className="viewtitle">Werkstatt-Board</h2>
        <p className="sub">Alle Aufträge · Status-Board</p>
      </div>

      <div className="wf-filterbar">
        <input
          className="wf-input"
          type="search"
          placeholder="Suchen: Nummer oder Notiz"
          aria-label="Aufträge durchsuchen"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <div className="wf-chips" role="group" aria-label="Filter">
          {WORKSHOP_FILTERS.map((option) => (
            <button
              key={option.key}
              type="button"
              className={filter === option.key ? 'wf-chip wf-chip--active' : 'wf-chip'}
              aria-pressed={filter === option.key}
              onClick={() => setFilter(option.key)}
            >
              {option.label}
            </button>
          ))}
        </div>
        <Link to="/new" className="wf-btn wf-btn--primary">
          ＋ Neuer Auftrag
        </Link>
      </div>

      <div className="wf-board">
        {WORKSHOP_COLUMNS.map((column) => {
          const items = columns[column.status]
          return (
            <div key={column.status} className="wf-col">
              <div className="wf-col__head">
                <span className={`wf-col__dot wf-col__dot--${column.status}`} aria-hidden="true" />
                <span className="wf-col__title">{column.title}</span>
                <span className="wf-col__count">{items.length}</span>
              </div>
              {items.length === 0 ? (
                <div className="wf-col__empty">Keine Aufträge</div>
              ) : (
                items.map((order) => <OrderCard key={order.id} order={order} />)
              )}
            </div>
          )
        })}
      </div>
    </section>
  )
}
